import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import './index.css';
import { localStorageKeys } from './constants/storage';
import { initSentry } from './config/sentry';

// Before anything else can throw. It was defined but never called, so every reportError() and
// error-boundary report went nowhere. No-op until VITE_SENTRY_DSN is set at build time.
initSentry();

// Handle chunk loading failures globally
const CHUNK_RECOVERY_KEY = localStorageKeys.chunkRecoveryAttempted;
const SW_UPDATE_RELOAD_KEY = localStorageKeys.swUpdateReloadAttempted;
const APP_VERSION_RELOAD_KEY = localStorageKeys.appVersionReloadAttempted;
const CURRENT_BUILD_VERSION = String(import.meta.env.VITE_APP_VERSION || 'development');
const CHUNK_LOAD_ERROR_PATTERN = /(error loading dynamically imported module|failed to fetch dynamically imported module|loading chunk|chunkloaderror)/i;

let triggerServiceWorkerUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // no-op
  }
}

function isChunkLoadFailureMessage(message: string): boolean {
  return CHUNK_LOAD_ERROR_PATTERN.test(String(message || ''));
}

// ── New-version reloads ────────────────────────────────────────────────────────
// A deploy used to reload every open tab within a minute — mid-sale on the POS included — and
// the guard meant to stop repeats was cleared on every page load, so a stale cached index.html
// could reload a tab every 60s forever. Now: reload only when it can't interrupt anyone (tab
// hidden, or nobody has touched it for IDLE_BEFORE_RELOAD_MS), and at most once per remote
// version per tab session.
const IDLE_BEFORE_RELOAD_MS = 2 * 60 * 1000;
let lastInteractionAt = Date.now();
for (const eventName of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
  window.addEventListener(eventName, () => { lastInteractionAt = Date.now(); }, { passive: true, capture: true });
}

let pendingReload: { key: string; target: string; updater: (() => Promise<unknown>) | null } | null = null;

function isSafeToReload(): boolean {
  return document.visibilityState === 'hidden' || Date.now() - lastInteractionAt >= IDLE_BEFORE_RELOAD_MS;
}

async function performPendingReload(): Promise<void> {
  if (!pendingReload || !isSafeToReload()) return;
  const { key, target, updater } = pendingReload;
  pendingReload = null;
  writeSession(key, target);
  try {
    await updater?.();
  } catch {
    // Best effort only.
  }
  window.location.reload();
}

/** Ask for a reload to `target` (a build version); it happens once it is safe, and only once. */
function requestReloadToLatestVersion(key: string, target: string, updater?: (() => Promise<unknown>) | null): void {
  if (readSession(key) === target) return; // already reloaded for this version — don't loop
  pendingReload ??= { key, target, updater: updater ?? null };
  void performPendingReload();
}

document.addEventListener('visibilitychange', () => { void performPendingReload(); });
window.setInterval(() => { void performPendingReload(); }, 15 * 1000);

// ── Chunk-load recovery ────────────────────────────────────────────────────────
// A missing lazy chunk (usually: the tab predates a deploy) is recovered by clearing the SW +
// caches and reloading. The old version returned early when already attempted but still
// reloaded from its `finally`, so a chunk that stayed missing reloaded the tab every 750ms.
const CHUNK_RECOVERY_COOLDOWN_MS = 5 * 60 * 1000;

async function attemptChunkRecovery(): Promise<void> {
  const lastAttempt = Number(readSession(CHUNK_RECOVERY_KEY) || 0);
  if (Date.now() - lastAttempt < CHUNK_RECOVERY_COOLDOWN_MS) {
    console.warn('Chunk recovery attempted recently; not reloading again.');
    return;
  }
  writeSession(CHUNK_RECOVERY_KEY, String(Date.now()));

  try {
    // Best-effort: clear SW + caches so we don't keep serving stale index.html
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // Reload anyway: a fresh index.html is the fix even if the cache clear failed.
  }
  window.setTimeout(() => window.location.reload(), 750);
}

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (reason && typeof reason === 'object' && 'message' in reason) {
    const message = String(reason.message);
    if (isChunkLoadFailureMessage(message)) {
      console.warn('Chunk loading failed, attempting recovery...');
      void attemptChunkRecovery();
    }
  }
});

if (import.meta.env.PROD) {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // A waiting service worker = a new build. Reload into it once it is safe.
      requestReloadToLatestVersion(SW_UPDATE_RELOAD_KEY, 'service-worker-update', () => updateSW(true));
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const checkForUpdate = () => {
        void registration.update();
      };

      window.setInterval(checkForUpdate, 60 * 1000);
      window.addEventListener('focus', checkForUpdate);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          checkForUpdate();
        }
      });
    },
  });

  triggerServiceWorkerUpdate = updateSW;

  const checkForBuildVersionUpdate = async () => {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'cache-control': 'no-cache',
        },
      });

      if (!response.ok) return;

      const payload = await response.json();
      const remoteVersion = typeof payload?.version === 'string' ? payload.version.trim() : '';
      if (!remoteVersion || remoteVersion === CURRENT_BUILD_VERSION) return;

      console.info('New frontend build detected, reloading current tab...', {
        current: CURRENT_BUILD_VERSION,
        remote: remoteVersion,
      });

      const updateCurrentServiceWorker = triggerServiceWorkerUpdate;

      requestReloadToLatestVersion(
        APP_VERSION_RELOAD_KEY,
        remoteVersion,
        updateCurrentServiceWorker ? () => updateCurrentServiceWorker(true) : null,
      );
    } catch {
      // Ignore transient network failures.
    }
  };

  window.setInterval(() => {
    void checkForBuildVersionUpdate();
  }, 60 * 1000);
  window.addEventListener('focus', () => {
    void checkForBuildVersionUpdate();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForBuildVersionUpdate();
    }
  });
  void checkForBuildVersionUpdate();

  let wasControlled = Boolean(navigator.serviceWorker?.controller);

  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (!wasControlled) {
      wasControlled = true;
      return; // First install taking control, no need to reload
    }

    // The new worker has already taken over (we only let it once reloading was safe).
    window.location.reload();
  });
}

// Force correct zoom level on iOS devices
if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    // Force the viewport to reset on page load
    const content = viewportMeta.getAttribute('content') || '';
    viewportMeta.setAttribute('content', content);
    
    // Prevent zoom on orientation change
    document.addEventListener('gesturestart', (e) => {
      e.preventDefault();
    });
  }
}

// Prerendered pages are hydrated so they stay on screen while their lazy route loads. Anything else
// renders fresh: the app shell has an empty root, and a fallback can serve one route's HTML for another.
const rootElement = document.getElementById('root')!
const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/'
const canonicalHref = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.href
const htmlMatchesRoute =
  !!canonicalHref && normalizePath(new URL(canonicalHref).pathname) === normalizePath(window.location.pathname)
// Prerendered HTML uses the default theme and currency, so a saved preference can't hydrate against it.
function hasSavedDisplayPreference(): boolean {
  try {
    const currency = localStorage.getItem('preferredCurrency')
    return localStorage.getItem('tcgeasy-theme') === 'dark' || (!!currency && currency !== 'AUD')
  } catch {
    return false
  }
}
const appTree = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
if (rootElement.hasChildNodes() && htmlMatchesRoute && !hasSavedDisplayPreference()) {
  ReactDOM.hydrateRoot(rootElement, appTree)
} else {
  ReactDOM.createRoot(rootElement).render(appTree)
}
